import { Delete } from 'icons';
import { useIntl } from 'localization';
import { Transforms } from 'slate';
import {
  ReactEditor,
  RenderElementProps,
  useFocused,
  useSelected,
  useSlateStatic,
} from 'slate-react';
import { twMerge } from 'tailwind-merge';
import { Tooltip } from 'ui';
import { IconButton } from 'ui/atoms';

import { useRichImageSrc } from '../RichImageContext';
import { CustomElement } from '../types';

const ImageComponent = ({ attributes, children, element }: RenderElementProps) => {
  const editor = useSlateStatic();
  const path = ReactEditor.findPath(editor, element);

  const { formatMessage } = useIntl();

  const selected = useSelected();
  const focused = useFocused();

  const resolveSrc = useRichImageSrc();
  const el = element as CustomElement;
  const src = resolveSrc(el);
  // Loader while the paste uploads, or while a keyed image is still being signed
  // (an unsigned private URL would 403).
  const showLoader = Boolean(el.uploading || (el.imageKey && !src));

  return (
    <span {...attributes} className="inline-block">
      {children}
      <span contentEditable={false} className="relative inline-block">
        {showLoader ? (
          <span className="my-1 flex h-[6em] w-[10em] items-center justify-center rounded-[10px] bg-v2blueGray-100 align-bottom">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-v2blueGray-300 border-t-v2blueGray-600" />
          </span>
        ) : (
          <img
            src={src}
            className={twMerge(
              'my-1 max-w-full max-h-[20em] rounded-[10px] align-bottom',
              selected && focused && 'ring-4 ring-v2blueGray-800/20',
            )}
          />
        )}
        <IconButton
          onClick={() => Transforms.removeNodes(editor, { at: path })}
          className={twMerge(
            'break-normal absolute top-[0.5em] left-[0.5em] w-9 h-9 bg-v2blueGray-200 text-v2blueGray-800  items-center justify-center',
            selected ? 'flex' : 'hidden',
          )}
        >
          <Tooltip title={formatMessage({ id: 'general.remove' })} placement="top">
            <Delete className="w-7 h-7" />
          </Tooltip>
        </IconButton>
      </span>
    </span>
  );
};

export default ImageComponent;
